/* Product-info replaces this custom element on a variant change, resetting to
   the new SKU's first image. All other views come from explicit Liquid data. */
(() => {
  if (!customElements.get('emberly-product-gallery')) {
    customElements.define('emberly-product-gallery', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        const link = this.querySelector('[data-em-image-open]');
        const image = link?.querySelector('img');
        const thumbnails = this.querySelector('[data-em-gallery-thumbnails]');
        if (!link || !image || !thumbnails) return;
        this.initialized = true;
        this.link = link;
        this.image = image;
        this.status = this.querySelector('[data-em-gallery-status]');
        this.label = this.dataset.productLabel || image.alt;
        let additionalViews = [];
        try { additionalViews = JSON.parse(this.dataset.gallery || '[]'); } catch { /* Keep the working first image. */ }
        if (!Array.isArray(additionalViews)) additionalViews = [];
        const seen = new Set([link.href]);
        additionalViews = additionalViews.flatMap(view => {
          if (!view || view.source !== 'styled' || typeof view.src !== 'string') return [];
          try {
            // Shopify file_url can be protocol-relative; normalize before validating.
            const url = new URL(view.src, document.baseURI);
            if (!['https:', 'http:'].includes(url.protocol) || seen.has(url.href)) return [];
            seen.add(url.href);
            return [{
              src: url.href,
              width: Number(view.width) > 0 ? Number(view.width) : 1254,
              label: typeof view.label === 'string' && view.label.trim() ? view.label : 'Product view',
              alt: typeof view.alt === 'string' && view.alt.trim() ? view.alt : '',
              source: 'styled',
              signature: view.signature !== false,
              embeddedSignature: view.embeddedSignature === true
            }];
          } catch { return []; }
        });
        this.views = [{ src: link.href, width: Number(this.dataset.imageWidth) || 1254, label: this.dataset.heroLabel || 'Studio view', source: 'studio', alt: link.dataset.emEmbeddedSignature === 'true' ? image.alt : '', signature: link.dataset.emHideSignature !== 'true', embeddedSignature: link.dataset.emEmbeddedSignature === 'true' }, ...additionalViews];
        // An explicit close-up uses the same pixels; it is not another photograph.
        // Only offer it for audited single-photo finishes, never in place of a real view.
        const detailSkus = new Set([
          'CJJJJTJT02975-Brown-US',
          'CJJT102445401AZ', 'CJJT102445404DW', 'CJJT102445410JQ', 'CJJT102445407GT',
          'CJYD202801402BY', 'CJJJJTCC01466-White-US',
          'CJYD309963502BY', 'CJYD309963505EV', 'CJYD309963508HS',
          'CJJT111158504DW',
          'CJJJJTJT18450-12 Brown', 'CJJJJTJT18450-12 yellow', 'CJJJJTJT18450-3 Brown',
          'CJJJJTJT18450-3 yellow', 'CJJJJTJT18450-6 Brown', 'CJJJJTJT18450-6 yellow'
        ]);
        if (this.views.length === 1 && detailSkus.has(this.dataset.variantSku)) {
          this.views.push({
            ...this.views[0],
            label: 'Detail from the same photo',
            alt: `${this.label} — enlarged detail of the same product photograph`,
            detail: true,
            signature: false
          });
          this.detailNote = document.createElement('p');
          this.detailNote.className = 'em-gallery-detail-note';
          this.detailNote.textContent = 'Enlarged detail from the same photograph.';
          this.detailNote.hidden = true;
          (this.querySelector('.em-studio-gallery__caption') || this).append(this.detailNote);
        }
        this.index = 0;
        this.buttons = this.views.map((view, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'em-gallery-thumbnail';
          button.dataset.emDetail = String(view.detail === true);
          button.setAttribute('aria-label', `View ${index + 1} of ${this.views.length}: ${view.label}`);
          button.setAttribute('aria-pressed', String(index === 0));
          const preview = document.createElement('img');
          const thumbnailUrl = new URL(view.src, document.baseURI);
          if (/\/(?:cdn\/shop|s\/files)\//.test(thumbnailUrl.pathname)) thumbnailUrl.searchParams.set('width', '180');
          preview.src = thumbnailUrl.href;
          preview.alt = '';
          preview.width = 88;
          preview.height = 88;
          preview.loading = 'lazy';
          button.append(preview);
          if (view.detail) {
            const badge = document.createElement('span');
            badge.className = 'em-gallery-thumbnail__detail';
            badge.textContent = 'Detail';
            badge.setAttribute('aria-hidden', 'true');
            button.append(badge);
          }
          button.addEventListener('click', () => this.select(index));
          button.addEventListener('keydown', event => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            let target;
            if (event.key === 'ArrowRight') target = (index + 1) % this.views.length;
            if (event.key === 'ArrowLeft') target = (index - 1 + this.views.length) % this.views.length;
            if (event.key === 'Home') target = 0;
            if (event.key === 'End') target = this.views.length - 1;
            if (target === undefined) return;
            event.preventDefault();
            this.select(target);
            this.buttons[target].focus({ preventScroll: true });
            this.buttons[target].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
          });
          thumbnails.append(button);
          return button;
        });
        this.querySelector('[data-em-gallery-previous]')?.addEventListener('click', () => this.select((this.index - 1 + this.views.length) % this.views.length));
        this.querySelector('[data-em-gallery-next]')?.addEventListener('click', () => this.select((this.index + 1) % this.views.length));
        thumbnails.hidden = this.views.length < 2;
        this.querySelector('[data-em-gallery-controls]').hidden = this.views.length < 2;
        this.updateStatus();
      }

      select(index) {
        const view = this.views[index];
        if (!view) return;
        this.index = index;
        this.image.src = view.src;
        const responsiveUrl = new URL(view.src, document.baseURI);
        if (/\/(?:cdn\/shop|s\/files)\//.test(responsiveUrl.pathname)) {
          const maxWidth = Math.min(view.width || 1254, 1254);
          this.image.srcset = [...[180, 360, 540, 720, 960].filter(width => width < maxWidth), maxWidth].map(width => { responsiveUrl.searchParams.set('width', String(width)); return `${responsiveUrl.href} ${width}w`; }).join(', ');
        } else {
          this.image.removeAttribute('srcset');
        }
        this.image.alt = view.alt || `${this.label} — ${view.label}`;
        this.link.href = view.src;
        this.link.dataset.emSource = view.source;
        this.link.dataset.emEmbeddedSignature = String(view.embeddedSignature);
        this.link.dataset.emHideSignature = String(view.signature === false);
        this.link.dataset.emDetail = String(view.detail === true);
        if (this.detailNote) this.detailNote.hidden = view.detail !== true;
        this.link.setAttribute('aria-label', `View larger image of ${this.label}: ${view.label}`);
        const signature = this.link.querySelector('.em-photo-signature');
        if (signature) signature.hidden = view.signature === false || view.embeddedSignature || !['studio', 'styled'].includes(view.source);
        this.buttons.forEach((button, position) => button.setAttribute('aria-pressed', String(position === index)));
        this.updateStatus();
      }

      updateStatus() {
        const view = this.views[this.index];
        this.link.dataset.emViewLabel = view.label;
        if (this.status) this.status.textContent = `${this.index + 1} / ${this.views.length} — ${view.label}`;
      }
    });
  }

  // The delegated zoom listener also covers galleries replaced after a variant change.
  let dialog;
  let returnFocus;
  let previousOverflow;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-em-image-open]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof HTMLDialogElement === 'undefined' || document.querySelector('dialog[open]')) return;
    event.preventDefault();
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'em-image-viewer';
      dialog.setAttribute('aria-label', 'Product photograph');
      dialog.innerHTML = '<button class="em-image-viewer__close" type="button" aria-label="Close product photograph">Close <span aria-hidden="true">×</span></button><div class="em-image-viewer__stage"><img alt="" width="1254" height="1254"><span class="em-photo-signature" aria-hidden="true">EMBERLY</span></div><p class="em-image-viewer__caption"></p>';
      document.body.append(dialog);
      dialog.querySelector('button').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (click) => {
        if (click.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (click.clientX < bounds.left || click.clientX > bounds.right || click.clientY < bounds.top || click.clientY > bounds.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.body.style.overflow = previousOverflow;
        if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      });
    }
    const original = link.querySelector('img');
    const photo = dialog.querySelector('img');
    photo.src = link.href;
    photo.alt = original.alt;
    dialog.dataset.emSource = link.dataset.emSource || 'studio';
    dialog.dataset.emDetail = link.dataset.emDetail || 'false';
    dialog.querySelector('.em-photo-signature').hidden = link.dataset.emHideSignature === 'true' || link.dataset.emEmbeddedSignature === 'true' || !['studio', 'styled'].includes(link.dataset.emSource || 'studio');
    dialog.querySelector('.em-image-viewer__caption').textContent = `${link.dataset.emViewLabel || 'Product view'}. Studio-styled product photograph. Watches and props are not included.`;
    returnFocus = link;
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
  });
})();
